import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSellerOrdersWithItems, subscribeToSellerMerchantOrders, updateGroupStatus, getGroupStatus, generateGroupId } from '../utils/multiVendorOrderApi';
import { BackButton } from '../components/ui';
import { SellerHeader } from '../components/SellerHeader';
import { formatOrderDate } from '../lib/dateUtils';
import { GRADIENT_UTILS } from '../lib/styleGuide';

const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

const ORDER_TABS = [
  { id: 'active', label: 'Active Orders' },
  { id: 'all', label: 'All Orders' },
  { id: 'new', label: 'New' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'in-progress', label: 'In Progress' },
  { id: 'ready', label: 'Ready' },
  { id: 'out-for-delivery', label: 'Out for Delivery' },
  { id: 'completed', label: 'Completed' },
  { id: 'canceled', label: 'Canceled' },
];

const STATUS_WORKFLOW = {
  placed: { tag: 'NEW', key: 'new', action: 'Accept Order', next: 'accepted' },
  accepted: { tag: 'ACCEPTED', key: 'accepted', action: 'Start Preparing', next: 'in-progress' },
  'in-progress-pickup': { tag: 'PREPARING', key: 'preparing', action: 'Mark as Ready', next: 'ready' },
  'in-progress-delivery': { tag: 'PREPARING', key: 'preparing', action: 'Start Delivery', next: 'out-for-delivery' },
  'ready': { tag: 'READY', key: 'ready', action: 'Mark as Complete', next: 'completed' },
  'out-for-delivery': { tag: 'DELIVERING', key: 'delivering', action: 'Mark as Complete', next: 'completed' },
  'completed': { tag: 'COMPLETED', key: 'completed', action: null, next: null },
  'canceled': { tag: 'CANCELED', key: 'canceled', action: null, next: null },
};

// Lighter bg and darker text for each status
const STATUS_TAG_STYLES = {
  new: { bg: '#F3E8FF', text: '#6D28D9' }, // light purple bg, dark purple text
  accepted: { bg: '#E3F2FD', text: '#1565C0' }, // light blue bg, dark blue text
  preparing: { bg: '#FFFDE7', text: '#F9A825' }, // light yellow bg, dark yellow text
  ready: { bg: '#FFFDE7', text: '#F9A825' }, // same as preparing
  delivering: { bg: '#FFF3E0', text: '#EF6C00' }, // light orange bg, dark orange text
  completed: { bg: '#E8F5E9', text: '#2E7D32' }, // light green bg, dark green text
  canceled: { bg: '#FFEBEE', text: '#C62828' }, // light red bg, dark red text
};

function getWorkflowKey(order) {
  // Map Firestore status to workflow keys used in STATUS_WORKFLOW
  switch (order.status) {
    case 'placed':
      return 'placed';
    case 'confirmed':
      return 'placed'; // treat confirmed as NEW
    case 'accepted':
      return 'accepted';
    case 'processing':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'in-progress':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'ready':
      return 'ready';
    case 'out-for-delivery':
      return 'out-for-delivery';
    case 'delivered':
    case 'completed':
      return 'completed';
    case 'cancelled':
    case 'canceled':
      return 'canceled';
    default:
      return order.status;
  }
}

export function Orders() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('active');
  const [updating, setUpdating] = useState({}); // {orderId: true/false}
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load orders function
  const loadOrders = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      setError('');
      console.log('🔍 Loading orders for seller:', currentUser.uid);
      
      const ordersData = await getSellerOrdersWithItems(currentUser.uid, 10);
      console.log('📦 Orders loaded:', ordersData.length, ordersData);
      
      setOrders(ordersData);
      setHasMore(ordersData.length === 10);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading orders:', err);
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Helper function to get date label
  const getDateLabel = useCallback((serviceDate) => {
    if (!serviceDate) return 'No date';
    
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Parse serviceDate as local date to avoid timezone issues
    // If serviceDate is in YYYY-MM-DD format, append time to ensure local interpretation
    const orderDate = serviceDate.includes('T') 
      ? new Date(serviceDate) 
      : new Date(serviceDate + 'T00:00:00');
    
    if (orderDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (orderDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return orderDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }, []);

  // Transform orders into groups by fulfillment date
  const ordersWithPreview = useMemo(() => {
    console.log('🔄 Transforming seller orders:', orders.length, orders);
    return orders.map(order => {
      console.log('🔍 Processing seller order:', order.id, 'Status:', order.status, 'Has status?', 'status' in order);
      console.log('📋 Order has orderItems?', !!order.orderItems, 'Length:', order.orderItems?.length);
      
      // Get normalized order status
      const orderStatus = (order.status || '').toUpperCase();
      const isOrderCanceled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED';
      console.log('📊 Order status check:', { orderStatus, isOrderCanceled });
      
      const preview = [];
      const sellerGroups = {};
      
      // Group items by service date
      if (order.orderItems && order.orderItems.length > 0) {
        order.orderItems.forEach(item => {
          const serviceDate = item.serviceDate || order.createdAt?.toDate?.()?.toISOString()?.split('T')[0];
          const groupKey = `${serviceDate}`;
          
          if (!sellerGroups[groupKey]) {
            sellerGroups[groupKey] = {
              serviceDate,
              items: [],
              total: 0,
              status: order.status || 'PLACED'
            };
          }
          
          sellerGroups[groupKey].items.push(item);
          sellerGroups[groupKey].total += (item.unitPrice || 0) * (item.quantity || 0);
        });
        
        // Convert groups to preview array
        Object.values(sellerGroups).forEach(group => {
          console.log('🔍 Orders page - Processing group:', group.serviceDate, 'with items:', group.items);
          
          // Check item statuses
          const itemStatuses = group.items.map(item => {
            const itemStatus = (item?.status || '').toUpperCase();
            return itemStatus || '';
          }).filter(s => s);
          
          const hasCanceledItem = itemStatuses.some(s => s === 'CANCELLED' || s === 'CANCELED');
          
          console.log('📊 Status check:', {
            orderStatus,
            isOrderCanceled,
            itemStatuses,
            itemCount: group.items.length,
            hasCanceledItem
          });
          
          // PRIORITY: If merchant order is CANCELED, the group is CANCELED (no need for getGroupStatus)
          let groupStatus;
          if (isOrderCanceled) {
            console.log('✅ Merchant order is CANCELED, setting group status to CANCELLED');
            groupStatus = 'CANCELLED';
          } else if (hasCanceledItem) {
            // Items are canceled even if merchant order isn't yet
            console.log('✅ Items are CANCELED, setting group status to CANCELLED');
            groupStatus = 'CANCELLED';
          } else {
            // Use getGroupStatus only if order and items are not canceled
            groupStatus = getGroupStatus(group.items, order.status);
          }
          
          console.log('✅ Orders page - Group status determined:', groupStatus);
          const groupId = generateGroupId(order.parentOrderId, currentUser.uid, group.serviceDate);
          console.log('🔍 Generated group ID:', groupId, 'from parentOrderId:', order.parentOrderId, 'sellerId:', currentUser.uid, 'serviceDate:', group.serviceDate);
          
          // Determine fulfillment method: delivery if shippingAddress exists, pickup otherwise
          const fulfillmentMethod = order.shippingAddress ? 'delivery' : 'pickup';
          
          preview.push({
            ...group,
            label_date: getDateLabel(group.serviceDate),
            seller: 'Your Store', // Since this is seller view
            groupId: groupId,
            status: groupStatus,
            fulfillmentMethod: fulfillmentMethod,
            merchantOrderId: order.id, // Add the merchant order ID for messaging
            parentOrderId: order.parentOrderId // Keep parent order ID for other uses
          });
        });
      }
      
      console.log('📋 Seller order preview created:', order.id, 'with', preview.length, 'groups');
      return {
        ...order,
        preview
      };
    });
  }, [orders, getDateLabel]);

  const filteredOrders = ordersWithPreview.map(order => {
    // Filter the preview groups based on the selected tab
    const filteredPreview = order.preview.filter(group => {
      if (tab === 'active') {
        // Debug: Log the actual status values
        console.log('🔍 Active filter - Group status:', group.status, 'Type:', typeof group.status);
        
        // Check for both uppercase and lowercase status values
        const isCompleted = group.status === 'COMPLETED' || group.status === 'completed';
        const isCanceled = group.status === 'CANCELLED' || group.status === 'CANCELED' || group.status === 'canceled' || group.status === 'cancelled';
        const shouldExclude = isCompleted || isCanceled;
        
        console.log('🔍 Active filter - Should exclude:', shouldExclude, 'isCompleted:', isCompleted, 'isCanceled:', isCanceled);
        return !shouldExclude;
      }
      if (tab === 'all') return true;
      if (tab === 'new') {
        // Exclude canceled orders from "new" tab
        const isCanceled = group.status === 'CANCELLED' || group.status === 'CANCELED' || group.status === 'canceled' || group.status === 'cancelled';
        return (group.status === 'PLACED' || group.status === 'placed') && !isCanceled;
      }
      if (tab === 'accepted') {
        // Exclude canceled orders from "accepted" tab
        const isCanceled = group.status === 'CANCELLED' || group.status === 'CANCELED' || group.status === 'canceled' || group.status === 'cancelled';
        return (group.status === 'ACCEPTED' || group.status === 'accepted') && !isCanceled;
      }
      if (tab === 'in-progress') return group.status === 'IN-PROGRESS' || group.status === 'in-progress';
      if (tab === 'ready') return group.status === 'READY' || group.status === 'ready';
      if (tab === 'out-for-delivery') return group.status === 'OUT-FOR-DELIVERY' || group.status === 'out-for-delivery';
      if (tab === 'completed') return group.status === 'COMPLETED' || group.status === 'completed';
      if (tab === 'canceled') return group.status === 'CANCELLED' || group.status === 'canceled' || group.status === 'cancelled';
      return true;
    });
    
    return {
      ...order,
      preview: filteredPreview
    };
  }).filter(order => order.preview.length > 0); // Only include orders that have at least one group after filtering

  // Flatten all groups from all orders for display
  const allGroups = filteredOrders.flatMap(order => 
    order.preview.map(group => ({
      ...group,
      parentOrder: order,
      parentOrderId: group.parentOrderId || order.parentOrderId // Use parent order ID, not merchant order ID
    }))
  ).sort((a, b) => {
    // First sort by Ready On date (serviceDate) - most current first
    const dateA = new Date(a.serviceDate);
    const dateB = new Date(b.serviceDate);
    
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime(); // Most current first (ascending)
    }
    
    // If Ready On dates are the same, sort by when placed (createdAt) - oldest first
    const placedA = new Date(a.parentOrder.createdAt?.toDate?.() || a.parentOrder.createdAt);
    const placedB = new Date(b.parentOrder.createdAt?.toDate?.() || b.parentOrder.createdAt);
    
    return placedA.getTime() - placedB.getTime(); // Oldest first (ascending)
  });

  const handleStatusUpdate = async (groupId, newStatus) => {
    setUpdating(u => ({ ...u, [groupId]: true }));
    try {
      console.log('🔄 Updating status for group:', groupId, 'to:', newStatus);
      console.log('🔍 updateGroupStatus function available:', typeof updateGroupStatus);
      
      const result = await updateGroupStatus(groupId, newStatus.toLowerCase());
      console.log('✅ Update result:', result);
      
      if (result.success) {
        // Show success message
        const statusMessages = {
          'accepted': 'Order group accepted!',
          'ready': 'Order group marked as ready!',
          'completed': 'Order group completed!',
          'cancelled': 'Order group cancelled!'
        };
        
        alert(statusMessages[newStatus.toLowerCase()] || 'Order status updated successfully!');
        
        // Reload orders to reflect changes
        await loadOrders();
      }
    } catch (err) {
      console.error('❌ Status update error:', err);
      alert(`Failed to update order status: ${err.message}`);
    } finally {
      setUpdating(u => ({ ...u, [groupId]: false }));
    }
  };

  const handleViewDetails = (group) => {
    navigate(`/order-details/${group.parentOrderId}`);
  };

  const handleMessageSeller = (group) => {
    // Redirect to seller messages page with order context
    // Use merchantOrderId for direct, consistent messaging
    const selectedOrderId = group.merchantOrderId; // Direct tie to merchant order
    console.log('🔍 Navigating to messages with merchantOrderId:', selectedOrderId);
    console.log('📋 Group data:', group);
    console.log('🎯 Using merchantOrderId for direct order-message connection');
    
    navigate('/seller-hub/messages', { 
      state: { 
        selectedOrderId: selectedOrderId,
        customerId: group.parentOrder?.buyerId,
        customerName: group.parentOrder?.buyerName
      } 
    });
  };

  const renderGroupCard = (group) => {
    // Determine workflow based on status and fulfillment method
    let workflowKey = group.status?.toLowerCase();
    if (workflowKey === 'in-progress') {
      workflowKey = group.fulfillmentMethod === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    }
    const workflow = STATUS_WORKFLOW[workflowKey] || {};
    const canCancel = !['completed', 'canceled', 'CANCELED', 'CANCELLED'].includes(group.status);

    return (
      <div key={group.groupId} className="bg-white rounded-xl shadow-md p-6 mb-4">
        {/* Group Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500 font-medium">
              Order #{group.parentOrderId.slice(-8).toUpperCase()}
            </span>
            <span className="font-semibold text-gray-900">{group.seller}</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
              Ready on: {group.label_date}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              group.status === 'PLACED' ? 'bg-gray-100 text-gray-800' :
              group.status === 'ACCEPTED' ? 'bg-yellow-100 text-yellow-800' :
              group.status === 'IN-PROGRESS' ? 'bg-orange-100 text-orange-800' :
              group.status === 'READY' ? 'bg-green-100 text-green-800' :
              group.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
              group.status === 'CANCELLED' || group.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {group.status === 'PLACED' ? 'PLACED' :
               group.status === 'ACCEPTED' ? 'ACCEPTED' :
               group.status === 'IN-PROGRESS' ? 'IN PREPARATION' :
               group.status === 'READY' ? 'READY' :
               group.status === 'COMPLETED' ? 'COMPLETED' :
               group.status === 'CANCELLED' || group.status === 'CANCELED' ? 'CANCELLED' :
               group.status}
            </span>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-gray-600">
              {group.items.length} item{group.items.length !== 1 ? 's' : ''}
            </div>
            <div className="text-lg font-bold text-gray-900">
              ${group.total.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Items in this group */}
        <div className="space-y-3 mb-4">
          {group.items.map((item, itemIndex) => (
            <div key={itemIndex} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center space-x-3 flex-1">
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                  {item.productSnapshot?.image ? (
                    <img 
                      src={item.productSnapshot.image} 
                      alt={item.productSnapshot.name || 'Product'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500 text-xs">No Image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">
                    {item.productSnapshot?.name || 'Product'}
                  </div>
                  {item.productSnapshot?.variant && Object.keys(item.productSnapshot.variant).length > 0 && (
                    <div className="text-gray-500 text-xs">
                      {Object.entries(item.productSnapshot.variant).map(([key, value]) => `${key}: ${value}`).join(', ')}
                    </div>
                  )}
                  <div className="text-gray-500 text-xs">
                    Qty: {item.quantity}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium text-gray-900 text-sm">
                  ${((item.unitPrice || 0) * (item.quantity || 0)).toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons for this group */}
        <div className="flex space-x-3">
          <button
            onClick={() => handleViewDetails(group)}
            style={{
              ...GRADIENT_UTILS.getGradientButton(),
              flex: 1,
              padding: '8px 16px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            View Details
          </button>
          <button
            onClick={() => handleMessageSeller(group)}
            className="flex items-center space-x-2 bg-white border border-[#C97B53] text-[#C97B53] py-2 px-4 rounded-lg hover:bg-[#C97B53] hover:text-white transition-colors text-sm font-medium"
          >
            <span>Message Customer</span>
          </button>
        </div>

        {/* Status Action Buttons */}
        {(workflow.action || canCancel) && (
          <div className="flex space-x-3 mt-3">
            {workflow.action && (
              <button
                onClick={() => handleStatusUpdate(group.groupId, workflow.next)}
                disabled={updating[group.groupId]}
                style={{
                  ...GRADIENT_UTILS.getGradientButton(),
                  flex: 1,
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  opacity: updating[group.groupId] ? 0.5 : 1,
                  cursor: updating[group.groupId] ? 'not-allowed' : 'pointer'
                }}
                onMouseOver={(e) => {
                  if (!updating[group.groupId]) {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                ✓ {workflow.action}
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => handleStatusUpdate(group.groupId, 'canceled')}
                disabled={updating[group.groupId]}
                className="flex-1 bg-white border border-red-500 text-red-500 py-2 px-4 rounded-lg hover:bg-red-500 hover:text-white transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                × Cancel
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  function getStatusColor(status) {
    switch (status) {
      case 'pending': return WARNING_ORANGE;
      case 'preparing': return PRIMARY_BROWN;
      case 'ready': return SUCCESS_GREEN;
      case 'delivered': return ACCENT_GRAY;
      case 'completed': return ACCENT_GRAY;
      default: return ACCENT_GRAY;
    }
  }
  // Using date-fns formatOrderDate utility
  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function formatOrderNumber(orderId) {
    // Extract last 6 characters of the order ID for a user-friendly number
    if (!orderId) return 'Order #000000';
    const shortId = orderId.slice(-6).toUpperCase();
    return `Order #${shortId}`;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 flex flex-col pt-16">
      <SellerHeader />
      {/* Filter Dropdown */}
      <div className="w-full bg-white sticky top-[64px] z-10 shadow-sm px-6 py-4 flex justify-start border-b border-gray-200">
        <div className="relative" style={{ minWidth: 200 }}>
          <select
            value={tab}
            onChange={e => setTab(e.target.value)}
            className="appearance-none border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C97B53] bg-white w-full"
          >
            {ORDER_TABS.map(tabObj => (
              <option key={tabObj.id} value={tabObj.id}>{tabObj.label}</option>
            ))}
          </select>
          {/* Custom arrow icon */}
          <svg className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {/* Order Cards List */}
      <main className="w-full max-w-4xl mx-auto flex-1 overflow-y-auto pt-8 pb-24">
        <div className="space-y-6 px-6">
          {loading ? (
            <div className="text-center text-gray-500 mt-20">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C97B53]"></div>
                <span>Loading orders…</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 mt-20">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="font-medium">Error loading orders</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          ) : allGroups.length === 0 ? (
            <div className="text-center text-gray-500 mt-20">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 max-w-md mx-auto">
                <p className="font-medium">No orders found</p>
                <p className="text-sm text-gray-600 mt-1">No orders match the selected status filter.</p>
              </div>
            </div>
          ) : (
            allGroups.map(group => renderGroupCard(group))
          )}
        </div>
      </main>
      
    </div>
  );
} 